import { useMemo, useState } from 'react';
import { useOrgFilter } from '@/store/defaults';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { payrollRegistry, type AccrualRegistryItem, type AccrualKind } from '@/api/payroll_registry';
import { orgs } from '@/api/catalogs';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import PeriodFilter, { useHrPeriodFilter, MONTHS_UK } from '@/shared/components/PeriodFilter';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const KIND_LABEL: Record<AccrualKind, string> = {
  MAIN: 'Основна', ADVANCE: 'Аванс', VACATION: 'Відпустка',
  SICK: 'Лікарняний', COMPENSATION: 'Компенсація', CIVIL: 'ЦПУ',
};
const KIND_BADGE: Record<AccrualKind, { bg: string; fg: string }> = {
  MAIN: { bg: '#dcfce7', fg: '#166534' },
  ADVANCE: { bg: '#dbeafe', fg: '#1e40af' },
  VACATION: { bg: '#fef9c3', fg: '#854d0e' },
  SICK: { bg: '#fee2e2', fg: '#991b1b' },
  COMPENSATION: { bg: '#f3e8ff', fg: '#6b21a8' },
  CIVIL: { bg: '#ffedd5', fg: '#9a3412' },
};
/** Таблиця документа за рядком реєстру (id рядка відпустки — це наказ). */
const ACCRUAL_TABLE: Record<AccrualRegistryItem['route'], string> = {
  payroll: 'doc.payrolls', vacation: 'doc.vacation_orders', sick: 'doc.sick_leaves',
  compensation: 'doc.vacation_compensations',
};
// Маршрут редактора за рядком. Для відпустки/лікарняного — на РОЗРАХУНОК
// (calculation_id), якщо є; інакше на наказ/лист.
function routeForRow(r: AccrualRegistryItem): string {
  switch (r.route) {
    case 'payroll': return `/payrolls/${r.id}`;
    case 'vacation': return r.calculation_id
      ? `/hr/vacation-calculations/${r.calculation_id}` : `/hr/vacation-orders/${r.id}`;
    case 'sick': return `/hr/sick-leaves/${r.id}`;
    case 'compensation': return `/hr/vacation-compensations/${r.id}`;
  }
}

// Випадаюче меню «Створити». Борги — НЕ вид нарахування: окремий пункт меню
// «Борги по ЗП» (рішення власника 2026-07-05).
const CREATE_OPTIONS: Array<{ kind: AccrualKind; label: string; to: string }> = [
  { kind: 'MAIN', label: 'Нарахування ЗП основне', to: '/payrolls/new?kind=MAIN' },
  { kind: 'ADVANCE', label: 'Нарахування авансу', to: '/payrolls/new?kind=ADVANCE' },
  { kind: 'CIVIL', label: 'Нарахування по ЦПУ', to: '/payrolls/new?kind=CIVIL' },
  { kind: 'VACATION', label: 'Нарахування відпускних', to: '/hr/vacation-calculations/new' },
  { kind: 'SICK', label: 'Нарахування лікарняного', to: '/hr/sick-leaves/new' },
  { kind: 'COMPENSATION', label: 'Компенсація невикор. відпустки', to: '/hr/vacation-compensations/new' },
];

export default function AccrualsRegistryPage() {
  const navigate = useNavigate();
  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgId, setOrgId] = useOrgFilter();
  const [kind, setKind] = useState<'' | AccrualKind>('');
  // Період: поля порожні → період шапки; заповнені → лише вони (ч.775/782).
  const per = useHrPeriodFilter();
  const [showCreate, setShowCreate] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  // 2026-08-23 (Норма #29): серверна пагінація (/accruals/page) + сортування;
  // orgId='' = «Усі» → по ВСIХ accessible org. Вкладення — для нарахувань ЗП
  // (doc.payrolls); підпис — sign-control doc.payrolls.
  const { datasource, total } = useInfiniteGridDatasource<AccrualRegistryItem>(
    (limit, offset, sort) => payrollRegistry.accrualsPage({
      organization_id: orgId || undefined,
      kind: kind || undefined,
      ...per.params,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, kind, ...per.key],
    { attachmentsType: 'doc.payrolls',
      // ч.1218: у реєстрі документи чотирьох таблиць — для «не проведено за задумом» тип за рядком.
      docType: (r) => ACCRUAL_TABLE[(r as AccrualRegistryItem).route] },
  );
  const sc = useSignControlInfinite<AccrualRegistryItem>('doc.payrolls', orgId);

  const cols = useMemo<ColDef<AccrualRegistryItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: AccrualRegistryItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 120 },
    {
      field: 'kind', headerName: 'Вид', width: 130,
      cellRenderer: (p: { data?: AccrualRegistryItem }) => p.data ? (
        <span style={{ background: KIND_BADGE[p.data.kind].bg, color: KIND_BADGE[p.data.kind].fg,
          borderRadius: 4, padding: '1px 6px', fontSize: '0.78rem', fontWeight: 600 }}>
          {KIND_LABEL[p.data.kind]}
        </span>
      ) : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 120,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      colId: 'period', headerName: 'Період', width: 130,
      valueGetter: (p) => p.data?.period_month
        ? `${MONTHS_UK[p.data.period_month]} ${p.data.period_year}` : (p.data ? '—' : ''),
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 170,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    {
      field: 'employee_name', headerName: 'Працівник', flex: 1, minWidth: 190,
      valueFormatter: (p) => p.data ? (p.value ?? '(усі)') : '',
    },
    {
      field: 'net', headerName: 'До виплати', width: 120, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'paid', headerName: 'Виплачено', width: 110, sortable: false, cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.data ? (p.value != null ? fmt2(p.value) : '—') : '',
    },
    {
      field: 'debt', headerName: 'Борг', width: 110, sortable: false, cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: AccrualRegistryItem }) => !p.data ? '' : p.data.debt == null ? '—'
        : Number(p.data.debt) > 0
          ? <span style={{ color: 'var(--badge-red-fg)', fontWeight: 600 }}>{fmt2(p.data.debt)}</span>
          : <span style={{ color: 'var(--badge-green-fg)' }}>0,00</span>,
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 100,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Нарахування виплат<TitleTotal total={total} /></h2>
        <div style={{ position: 'relative' }}>
          <button className="primary" onClick={() => setShowCreate((s) => !s)}>
            + Створити ▾
          </button>
          {showCreate && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20,
              background: 'var(--bg-panel)', border: '1px solid var(--border-strong)', borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 240, overflow: 'hidden',
            }}>
              {CREATE_OPTIONS.map((o) => (
                <button key={o.kind} onClick={() => { setShowCreate(false); navigate(o.to); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none',
                    background: 'none', padding: '0.5rem 0.75rem', cursor: 'pointer', fontWeight: 500 }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value as '' | AccrualKind)} style={{ width: 160 }}>
          <option value="">усі види</option>
          <option value="MAIN">Основна</option>
          <option value="ADVANCE">Аванс</option>
          <option value="VACATION">Відпустка</option>
          <option value="SICK">Лікарняний</option>
          <option value="COMPENSATION">Компенсація</option>
          <option value="CIVIL">ЦПУ</option>
        </select>
        <PeriodFilter {...per.bind} />
      </div>

      <InfiniteDocGrid<AccrualRegistryItem>
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="accruals-registry"
        onRowDoubleClicked={(r) => navigate(routeForRow(r))}
        getRowStyle={sc.getRowStyle}
      />
    </div>
  );
}
